import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import produccionProgramacionService from '../../services/produccionProgramacionService';
import { getApiErrorMessage } from '../../utils/apiError';

type Props = {
  codigo: string;
  descripcion: string;
  open: boolean;
  onClose: () => void;
};

const texto = (valor: number | null | undefined) => (valor == null ? '' : String(valor));

const numeroONull = (valor: string) => {
  if (valor.trim() === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};

const ProgramacionParametrosDialog: React.FC<Props> = ({ codigo, descripcion, open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const cerrarRef = useRef(onClose);
  cerrarRef.current = onClose;
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [stockSeguridad, setStockSeguridad] = useState('');
  const [loteMinimo, setLoteMinimo] = useState('');
  const [loteMultiplo, setLoteMultiplo] = useState('');
  const [leadTimeDias, setLeadTimeDias] = useState('');
  const [coberturaObjetivoDias, setCoberturaObjetivoDias] = useState('');
  const [activo, setActivo] = useState(true);

  useEffect(() => {
    if (!open) return;
    let vigente = true;
    setCargando(true);
    produccionProgramacionService.getParametros(codigo)
      .then((parametros) => {
        if (!vigente) return;
        setStockSeguridad(texto(parametros.stockSeguridad));
        setLoteMinimo(texto(parametros.loteMinimo));
        setLoteMultiplo(texto(parametros.loteMultiplo));
        setLeadTimeDias(texto(parametros.leadTimeDias));
        setCoberturaObjetivoDias(texto(parametros.coberturaObjetivoDias));
        setActivo(parametros.activo);
      })
      .catch((error) => {
        if (!vigente) return;
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron leer los parámetros.'), { variant: 'error' });
        cerrarRef.current();
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [codigo, enqueueSnackbar, open]);

  const guardar = async () => {
    try {
      setGuardando(true);
      await produccionProgramacionService.guardarParametros({
        codigoArticulo: codigo,
        stockSeguridad: numeroONull(stockSeguridad),
        loteMinimo: numeroONull(loteMinimo),
        loteMultiplo: numeroONull(loteMultiplo),
        leadTimeDias: numeroONull(leadTimeDias),
        coberturaObjetivoDias: numeroONull(coberturaObjetivoDias),
        activo,
      });
      enqueueSnackbar('Parámetros guardados. Volvé a calcular para aplicarlos.', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron guardar los parámetros.'), { variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Parámetros de fabricación</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {codigo} — {descripcion}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Un campo vacío deja el valor de siempre: el mínimo del artículo, el horizonte del cálculo y sin lote ni lead time.
            El factor estacional (mismo mes del año pasado, entre 0,5 y 2) se aplica al calcular, aunque estos campos estén vacíos.
          </Typography>
          {cargando ? <CircularProgress size={22} /> : (
            <>
              <TextField
                label="Stock de seguridad"
                type="number"
                size="small"
                value={stockSeguridad}
                onChange={(e) => setStockSeguridad(e.target.value)}
                helperText="Si lo cargás, reemplaza el stock mínimo."
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Lote mínimo"
                type="number"
                size="small"
                value={loteMinimo}
                onChange={(e) => setLoteMinimo(e.target.value)}
                helperText="Si la necesidad es menor, sube hasta este lote."
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Múltiplo de lote"
                type="number"
                size="small"
                value={loteMultiplo}
                onChange={(e) => setLoteMultiplo(e.target.value)}
                helperText="La cantidad sugerida se redondea hacia arriba a este múltiplo."
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Lead time (días)"
                type="number"
                size="small"
                value={leadTimeDias}
                onChange={(e) => setLeadTimeDias(e.target.value)}
                helperText="La fecha sugerida se adelanta estos días y no queda antes de hoy."
                inputProps={{ min: 0, max: 365 }}
              />
              <TextField
                label="Cobertura objetivo (días)"
                type="number"
                size="small"
                value={coberturaObjetivoDias}
                onChange={(e) => setCoberturaObjetivoDias(e.target.value)}
                helperText="Reemplaza el horizonte del cálculo solo para este artículo."
                inputProps={{ min: 1, max: 365 }}
              />
              <FormControlLabel
                control={<Checkbox checked={activo} onChange={(e) => setActivo(e.target.checked)} />}
                label="Usar estos parámetros en el cálculo"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={() => void guardar()} disabled={cargando || guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgramacionParametrosDialog;
